<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import Modal from 'dashboard/components/Modal.vue';
import format from 'date-fns/format';
import parseISO from 'date-fns/parseISO';
import formatDistanceToNow from 'date-fns/formatDistanceToNow';
import { useDateFnsLocale } from 'dashboard/composables/useDateFnsLocale';

const props = defineProps({
  execution: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['close']);

const { t } = useI18n();
const { dateFnsLocale } = useDateFnsLocale();
const showModal = ref(true);

const STATUS_ICONS = {
  completed: { icon: 'checkmark-circle', class: 'text-green-500' },
  failed: { icon: 'error-circle', class: 'text-red-500' },
  running: { icon: 'arrow-sync', class: 'text-blue-500' },
  timeout: { icon: 'clock', class: 'text-yellow-500' },
  cancelled: { icon: 'dismiss-circle', class: 'text-gray-500' },
};

const ACTION_ICONS = {
  send_message: 'chat',
  send_template: 'mail-template',
  assign_agent: 'person',
  assign_team: 'people-team',
  add_tag: 'tag',
  remove_tag: 'tag-dismiss',
  add_to_funnel: 'board',
  move_funnel_stage: 'arrow-right',
  create_product: 'box',
  create_appointment: 'calendar',
  create_note: 'note',
  schedule_message: 'clock',
  schedule_followup: 'calendar-clock',
  close_conversation: 'checkmark',
  reopen_conversation: 'arrow-undo',
  send_webhook: 'globe',
  http_request: 'globe',
  llm_agent: 'bot',
};

const statusIcon = computed(() => {
  return STATUS_ICONS[props.execution.status] || STATUS_ICONS.cancelled;
});

const executionLog = computed(() => {
  return props.execution.execution_log || [];
});

const durationText = computed(() => {
  if (!props.execution.duration_ms) return '-';
  const seconds = (props.execution.duration_ms / 1000).toFixed(1);
  return `${seconds}s`;
});

const formatDateTime = dateString => {
  if (!dateString) return '-';
  return format(parseISO(dateString), 'dd/MM/yyyy HH:mm:ss');
};

const formatRelativeTime = dateString => {
  if (!dateString) return '';
  return formatDistanceToNow(parseISO(dateString), {
    addSuffix: true,
    locale: dateFnsLocale.value,
  });
};

const getActionIcon = actionType => {
  return ACTION_ICONS[actionType] || 'flash';
};

const isStepSuccess = step => {
  return step.result?.success === true || step.status === 'success';
};

const isStepError = step => {
  return (
    step.result?.error ||
    step.result?.success === false ||
    step.status === 'error'
  );
};

const getStepError = step => {
  if (typeof step.result?.error === 'string') return step.result.error;
  if (step.result?.error_message) return step.result.error_message;
  return null;
};

const onClose = () => {
  emit('close');
};
</script>

<template>
  <Modal v-model:show="showModal" :on-close="onClose">
    <div class="flex flex-col max-h-[80vh]">
      <!-- Header -->
      <div class="p-4 border-b border-slate-200 dark:border-slate-700">
        <div class="flex items-center justify-between mb-2">
          <div class="flex items-center gap-2">
            <fluent-icon
              icon="arrow-swap"
              size="18"
              class="text-slate-600 dark:text-slate-300"
            />
            <h3
              class="text-base font-semibold text-slate-800 dark:text-slate-100"
            >
              {{ execution.flow?.name || t('FLOW_EXECUTIONS.UNKNOWN_FLOW') }}
            </h3>
          </div>
          <button
            class="p-1 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 rounded"
            @click="onClose"
          >
            <fluent-icon icon="dismiss" size="16" />
          </button>
        </div>

        <!-- Meta info -->
        <div
          class="flex flex-wrap gap-3 text-xs text-slate-500 dark:text-slate-400"
        >
          <div class="flex items-center gap-1">
            <fluent-icon
              :icon="statusIcon.icon"
              size="14"
              :class="statusIcon.class"
            />
            <span class="font-medium">{{
              t(`FLOW_EXECUTIONS.STATUS.${execution.status.toUpperCase()}`)
            }}</span>
          </div>
          <div class="flex items-center gap-1">
            <fluent-icon icon="clock" size="14" />
            <span>{{ durationText }}</span>
          </div>
          <div class="flex items-center gap-1">
            <fluent-icon icon="calendar" size="14" />
            <span :title="formatDateTime(execution.started_at)">
              {{ formatRelativeTime(execution.started_at) }}
            </span>
          </div>
          <div class="flex items-center gap-1">
            <fluent-icon icon="flash" size="14" />
            <span>{{ execution.trigger_event }}</span>
          </div>
        </div>
      </div>

      <!-- Error Banner -->
      <div
        v-if="execution.error_message"
        class="mx-4 mt-3 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg"
      >
        <div class="flex items-start gap-2">
          <fluent-icon
            icon="error-circle"
            size="16"
            class="text-red-500 shrink-0 mt-0.5"
          />
          <div>
            <p
              class="text-xs font-medium text-red-700 dark:text-red-300 mb-0.5"
            >
              {{ t('FLOW_EXECUTIONS.LOG_MODAL.ERROR_TITLE') }}
            </p>
            <p class="text-xs text-red-600 dark:text-red-400">
              {{ execution.error_message }}
            </p>
          </div>
        </div>
      </div>

      <!-- Execution Timeline -->
      <div class="flex-1 overflow-y-auto p-4">
        <h4
          v-if="executionLog.length > 0"
          class="text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider mb-3"
        >
          {{ t('FLOW_EXECUTIONS.LOG_MODAL.TIMELINE') }}
        </h4>

        <div v-if="executionLog.length > 0" class="space-y-1">
          <div
            v-for="(step, index) in executionLog"
            :key="index"
            class="flex gap-3 py-2 px-2 rounded-md"
            :class="{
              'bg-red-50/50 dark:bg-red-900/10': isStepError(step),
            }"
          >
            <!-- Step number -->
            <div class="flex flex-col items-center shrink-0">
              <div
                class="w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium"
                :class="
                  isStepError(step)
                    ? 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300'
                    : 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300'
                "
              >
                {{ index + 1 }}
              </div>
              <div
                v-if="index < executionLog.length - 1"
                class="w-px h-full mt-1 bg-slate-200 dark:bg-slate-700"
              />
            </div>

            <!-- Step content -->
            <div class="flex-1 min-w-0 pb-1">
              <div class="flex items-center gap-2 mb-0.5">
                <fluent-icon
                  :icon="getActionIcon(step.action_type || step.node_id)"
                  size="14"
                  class="text-slate-500 dark:text-slate-400"
                />
                <span
                  class="text-sm font-medium text-slate-700 dark:text-slate-200"
                >
                  {{
                    step.action_type ||
                    step.node_id ||
                    t('FLOW_EXECUTIONS.LOG_MODAL.UNKNOWN_ACTION')
                  }}
                </span>
                <fluent-icon
                  v-if="isStepSuccess(step)"
                  icon="checkmark-circle"
                  size="14"
                  class="text-green-500"
                />
                <fluent-icon
                  v-else-if="isStepError(step)"
                  icon="error-circle"
                  size="14"
                  class="text-red-500"
                />
              </div>

              <!-- Step error -->
              <div
                v-if="getStepError(step)"
                class="mt-1 px-2 py-1.5 bg-red-100 dark:bg-red-900/30 rounded text-xs text-red-700 dark:text-red-300"
              >
                {{ getStepError(step) }}
              </div>

              <!-- Timestamp -->
              <div
                v-if="step.executed_at || step.timestamp"
                class="text-xs text-slate-400 dark:text-slate-500 mt-0.5"
              >
                {{ formatDateTime(step.executed_at || step.timestamp) }}
              </div>
            </div>
          </div>
        </div>

        <!-- Empty log -->
        <div v-else class="text-center py-8">
          <fluent-icon
            icon="document"
            size="28"
            class="text-slate-300 dark:text-slate-600 mb-2"
          />
          <p class="text-sm text-slate-500 dark:text-slate-400">
            {{ t('FLOW_EXECUTIONS.LOG_MODAL.NO_LOGS') }}
          </p>
        </div>
      </div>
    </div>
  </Modal>
</template>
